import React, { useMemo, useCallback } from 'react';
import ProductCard from '@prisjakt/ui-product-card';
import ProductImage from '@prisjakt/ui-product-image';
import Lazy from '../../../../../components/lazy';
import { CampaignIdsByProductId } from '../../../../../hooks/campaigns-by-product-id';
import { Product, AdvizedContentFragment } from '../../../../../types';
import { trackProductCollectionProductClick } from '../../../tracking';
import { AdsGridContainer } from '../../productOffersContainer/container-ads-grid';
import { ProductComparisonProps } from '../types';
import ActiveCampaignBadge from '../../active-campaign-badge';
import HighlightedSearchResult from '../highlighted-search-result';

const IMAGES_PER_ROW_MOBILE = 2;
const NO_CAMPAIGNS: never[] = [];

type ProductCardWrapperProps = {
  product: Product;
  productCampaigns?: number[];
  lazyLoadImage?: boolean;
} & ProductComparisonProps;

const ProductCardWrapper = ({
  product,
  isSelectingProducts,
  selectedProducts,
  selectProductById,
  deselectProductById,
  productCampaigns = NO_CAMPAIGNS,
  lazyLoadImage = true,
}: ProductCardWrapperProps) => {
  const image = useMemo(() => {
    const imageEl = (
      <ProductImage
        name={product.name}
        src={(product.media && product.media.first) || ''}
        fallback={product.category?.logo || undefined}
      />
    );
    return lazyLoadImage ? <Lazy offset={0}>{imageEl}</Lazy> : imageEl;
  }, [product.name, product.media, product.category, lazyLoadImage]);
  const { id } = product;
  const onClickHandler = useCallback(
    () => trackProductCollectionProductClick(id),
    [id]
  );
  const onSelect = useCallback(() => selectProductById(id, 'productCard'), [
    id,
    selectProductById,
  ]);
  const onDeselect = useCallback(() => deselectProductById(id, 'productCard'), [
    id,
    deselectProductById,
  ]);
  const campaignBadge = useMemo(
    () => (
      <ActiveCampaignBadge includedCampaigns={productCampaigns} size="large" />
    ),
    [productCampaigns]
  );

  return (
    <ProductCard
      link={product.pathName}
      onClick={onClickHandler}
      name={product.name}
      image={image}
      price={product.priceSummary && product.priceSummary.regular}
      rating={product.userReviewSummary && product.userReviewSummary.rating}
      ratingCount={product.userReviewSummary && product.userReviewSummary.count}
      selectable={isSelectingProducts}
      selected={selectedProducts.some(p => p.id === id)}
      onSelect={onSelect}
      onDeselect={onDeselect}
      campaignBadge={campaignBadge}
      highlightedName={
        product.highlight ? (
          <HighlightedSearchResult>{product.highlight}</HighlightedSearchResult>
        ) : (
          undefined
        )
      }
    />
  );
};

type Props = {
  products: Product[];
  advized: AdvizedContentFragment | null;
  campaignsByProduct: CampaignIdsByProductId;
} & ProductComparisonProps;
export const GridLayout = ({
  products,
  advized,
  campaignsByProduct,
  ...productComparisonProps
}: Props) => {
  return (
    <AdsGridContainer advized={advized}>
      {products.map((product, index) => (
        <ProductCardWrapper
          key={product.id}
          product={product}
          productCampaigns={campaignsByProduct[product.id]}
          // Lazy loading is disabled for the first row of images (two first for mobile)
          // to enable images to load before React is loaded. The goal of this strategy
          // is to improve our Lighthouse performance score, and specifically the
          // "Largest Contentful Paint" metric.
          lazyLoadImage={index >= IMAGES_PER_ROW_MOBILE * 2}
          {...productComparisonProps}
        />
      ))}
    </AdsGridContainer>
  );
};
